import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import {
  Avatar,
  Button,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListSubheader,
  ListItemText,
  Toolbar,
  Container,
  Box,
  CssBaseline,
  Typography,
  IconButton,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import DeliveryDiningRoundedIcon from '@mui/icons-material/DeliveryDiningRounded';
import DvrRoundedIcon from '@mui/icons-material/DvrRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RestaurantMenuRoundedIcon from '@mui/icons-material/RestaurantMenuRounded';
import RoomServiceRoundedIcon from '@mui/icons-material/RoomServiceRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import TableRestaurantRoundedIcon from '@mui/icons-material/TableRestaurantRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import Pedidos, { AutoPrintRuntime } from './pages/Pedidos';
import Avaliacoes from './pages/Avaliacoes';
import Balcao from './pages/Balcao';
import Salao from './pages/Salao';
import Garcom from './pages/Garcom';
import WaiterAccess from './pages/WaiterAccess';
import WaiterOperation from './pages/WaiterOperation';
import Cardapio from './pages/Estoque';
import Estoque from './pages/Insumos';
import Clientes from './pages/Clientes';
import Funcionarios from './pages/Funcionarios';
import Financeiro from './pages/Financeiro';
import Configuracoes from './pages/Configuracoes';
import CatalogHomologacao from './pages/CatalogHomologacao';
import Kds from './pages/Kds';
import KdsAccess from './pages/KdsAccess';
import KdsMonitor from './pages/KdsMonitor';
import Login from './pages/Login';
import TrocarSenha from './pages/TrocarSenha';
import RecuperarSenha from './pages/RecuperarSenha';
import RedefinirSenha from './pages/RedefinirSenha';
import SuiteModuleLauncher from './pages/SuiteModuleLauncher';
import PublicTableMenu from './pages/PublicTableMenu';
import ProtectedRoute from './components/ProtectedRoute';
import BillingNoticeBanner from './components/BillingNoticeBanner';
import NotificationBell from './components/NotificationBell';
import { PageMenuContext } from './components/PageMenuButton';
import { useAuth } from './auth/AuthContext';
import { getApiUrl } from './utils/api';
import { apiFetch } from './utils/apiFetch';
import {
  canAccessBackoffice,
  canAccessFinance,
  canAccessIfoodCatalog,
  canAccessSalon,
  canAccessWaiter,
} from './utils/auth';
import { getKdsDeviceSession } from './utils/kdsDeviceApi';
import './App.css';

const drawerWidth = 296;
const API_URL = getApiUrl();
const inlineMenuRoutes = new Set([
  '/avaliacoes', '/salao', '/garcom', '/kds', '/cardapio', '/estoque',
  '/clientes', '/funcionarios', '/financeiro', '/configuracoes', '/catalog',
]);

const sidebarVisualsByPath = {
  '/pedidos': { icon: ReceiptLongRoundedIcon, tone: 'orange' },
  '/balcao': { icon: PointOfSaleRoundedIcon, tone: 'blue' },
  '/salao': { icon: TableRestaurantRoundedIcon, tone: 'mint' },
  '/cardapio': { icon: RestaurantMenuRoundedIcon, tone: 'purple' },
  '/financeiro': { icon: AccountBalanceWalletRoundedIcon, tone: 'green' },
  '/kds': { icon: DvrRoundedIcon, tone: 'orange' },
  '/garcom': { icon: RoomServiceRoundedIcon, tone: 'blue' },
  '/estoque': { icon: Inventory2RoundedIcon, tone: 'blue' },
  '/clientes': { icon: GroupsRoundedIcon, tone: 'mint' },
  '/funcionarios': { icon: BadgeRoundedIcon, tone: 'purple' },
  '/avaliacoes': { icon: StarRoundedIcon, tone: 'amber' },
  '/catalog': { icon: StorefrontRoundedIcon, tone: 'rose' },
  '/configuracoes': { icon: SettingsRoundedIcon, tone: 'slate' },
};

const sidebarVisualsByLabel = {
  Entregas: { icon: DeliveryDiningRoundedIcon, tone: 'mint' },
  'Cardápio Digital': { icon: LanguageRoundedIcon, tone: 'purple' },
  Zap: { icon: ChatBubbleRoundedIcon, tone: 'green' },
};

const getSidebarVisual = (item) => (
  sidebarVisualsByPath[item.path]
  || sidebarVisualsByLabel[item.label]
  || { icon: AppsRoundedIcon, tone: 'slate' }
);

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

function AppShell() {
  const auth = useAuth();
  const theme = useTheme();
  const isMobileSidebar = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [suiteModules, setSuiteModules] = useState([]);
  const location = useLocation();
  const publicAuthPaths = ['/login', '/recuperar-senha', '/redefinir-senha'];
  const isPublicTableMenu = location.pathname === '/mesa';
  const isKdsAccess = location.pathname === '/kds/acesso';
  const isKdsMonitor = location.pathname === '/kds/monitor';
  const isKdsDeviceMonitor = isKdsMonitor
    && Boolean(getKdsDeviceSession());
  const isWaiterStandalone = location.pathname === '/garcom/acesso'
    || location.pathname === '/garcom/operacao';
  const isStandaloneRoute = isPublicTableMenu || isKdsAccess || isKdsDeviceMonitor || isWaiterStandalone;
  const showAppNavigation = auth.isAuthenticated
    && !publicAuthPaths.includes(location.pathname)
    && !isStandaloneRoute;
  const canRunAutoPrintRuntime = showAppNavigation
    && canAccessBackoffice(auth.user)
    && !location.pathname.startsWith('/kds')
    && !location.pathname.startsWith('/garcom');
  const defaultAuthenticatedPath = canAccessBackoffice(auth.user)
    ? '/pedidos'
    : canAccessSalon(auth.user) ? '/salao' : '/garcom';
  const canBackoffice = canAccessBackoffice(auth.user);

  useEffect(() => {
    let active = true;

    const loadSuiteModules = async () => {
      if (!auth.isAuthenticated || !canBackoffice) {
        setSuiteModules([]);
        return;
      }

      try {
        const response = await apiFetch(`${API_URL}/api/suite/modules`);
        const data = await response.json().catch(() => ({}));
        if (active) {
          setSuiteModules(response.ok && Array.isArray(data.modules) ? data.modules : []);
        }
      } catch {
        if (active) setSuiteModules([]);
      }
    };

    loadSuiteModules();
    const refreshVisibleModules = () => {
      if (document.visibilityState === 'visible') loadSuiteModules();
    };
    window.addEventListener('focus', loadSuiteModules);
    document.addEventListener('visibilitychange', refreshVisibleModules);
    return () => {
      active = false;
      window.removeEventListener('focus', loadSuiteModules);
      document.removeEventListener('visibilitychange', refreshVisibleModules);
    };
  }, [auth.isAuthenticated, canBackoffice]);

  const menuItems = useMemo(() => {
    const items = [];
    const canWaiter = canAccessWaiter(auth.user);
    const canSalon = canAccessSalon(auth.user);
    const canFinance = canAccessFinance(auth.user);
    const canIfoodCatalog = canAccessIfoodCatalog(auth.user);

    if (canBackoffice) {
      items.push(
        { label: 'Pedidos', path: '/pedidos' },
        { label: 'Balcão', path: '/balcao' },
      );
    }

    if (canSalon) {
      items.push({ label: 'Salão', path: '/salao' });
    }

    if (canBackoffice) {
      items.push({ label: 'Cardápio', path: '/cardapio' });
    }

    if (canFinance) {
      items.push({ label: 'Financeiro', path: '/financeiro' });
    }

    if (canBackoffice) {
      items.push({ label: 'KDS', path: '/kds' });
    }

    if (canWaiter) {
      items.push({ label: 'Garçom', path: '/garcom' });
    }

    if (canBackoffice) {
      items.push(
        { label: 'Estoque', path: '/estoque' },
        { label: 'Clientes', path: '/clientes' },
        { label: 'Funcionários', path: '/funcionarios' },
        { label: 'Avaliações', path: '/avaliacoes' },
      );
    }

    if (canIfoodCatalog) {
      items.push({ label: 'Cardápio iFood', path: '/catalog' });
    }

    if (canBackoffice) {
      items.push({ label: 'Configurações', path: '/configuracoes' });
    }

    return items;
  }, [auth.user, canBackoffice]);

  const suiteModuleItems = useMemo(
    () => suiteModules.map((module) => ({ label: module.label, path: module.path })),
    [suiteModules]
  );

  const isActiveRoute = (path) => location.pathname === path;

  const handleSidebarNavigation = () => {
    setSidebarOpen(false);
  };

  const showMainToolbar = showAppNavigation
    && location.pathname !== '/pedidos'
    && location.pathname !== '/balcao'
    && !inlineMenuRoutes.has(location.pathname)
    && location.pathname !== '/trocar-senha';
  const hasInlinePageMenu = showAppNavigation && inlineMenuRoutes.has(location.pathname);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {showAppNavigation && location.pathname === '/pedidos' ? <NotificationBell enabled /> : null}
      {canRunAutoPrintRuntime ? (
        <AutoPrintRuntime
          tenantKey={String(auth.user?.companyId || '')}
          sessionToken={auth.token}
        />
      ) : null}
      <Drawer
        className="brutos-sidebar-drawer"
        variant={isMobileSidebar ? 'temporary' : 'persistent'}
        open={showAppNavigation && sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: !isMobileSidebar && sidebarOpen ? drawerWidth : 0,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: isMobileSidebar ? 'min(296px, calc(100vw - 24px))' : drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box className="brutos-sidebar-brand">
          <Box
            component="img"
            src="/brutos-gestor-mark.png"
            alt=""
            aria-hidden="true"
            className="brutos-sidebar-brand-mark"
          />
          <Box className="brutos-sidebar-wordmark">
            <Typography component="span" className="brutos-sidebar-brand-name">
              BRUTOS
            </Typography>
            <Typography component="span" className="brutos-sidebar-brand-product">
              GESTOR
            </Typography>
          </Box>
          <IconButton
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu lateral"
            className="brutos-sidebar-close"
          >
            <ChevronLeftRoundedIcon />
          </IconButton>
        </Box>

        {auth.user ? (
          <Box className="brutos-sidebar-context">
            <Box className="brutos-sidebar-context-icon" aria-hidden="true">
              <StorefrontRoundedIcon />
            </Box>
            <Box className="brutos-sidebar-context-copy">
              <Typography className="brutos-sidebar-context-title">
                Gestor Delivery
              </Typography>
              <Typography className="brutos-sidebar-context-login" noWrap>
                {auth.user.login}
              </Typography>
              <Typography className="brutos-sidebar-context-role" noWrap>
                {auth.user.role}
              </Typography>
            </Box>
          </Box>
        ) : null}

        <Box component="nav" className="brutos-sidebar-navigation" aria-label="Navegação principal">
          <List className="brutos-sidebar-list" disablePadding>
          {menuItems.map((item) => (
            (() => {
              const visual = getSidebarVisual(item);
              const SidebarIcon = visual.icon;
              const active = isActiveRoute(item.path);

              return (
                <ListItem key={item.path} disablePadding className="brutos-sidebar-list-item">
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    selected={active}
                    aria-current={active ? 'page' : undefined}
                    onClick={handleSidebarNavigation}
                    className={`brutos-sidebar-link brutos-sidebar-link--${visual.tone}`}
                  >
                    <ListItemIcon className="brutos-sidebar-list-icon">
                      <Box component="span" className="brutos-sidebar-icon-tile">
                        <SidebarIcon fontSize="small" />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      slotProps={{ primary: { className: 'brutos-sidebar-link-label' } }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })()
          ))}
          {suiteModuleItems.length ? (
            <>
              <Divider className="brutos-sidebar-divider" />
              <ListSubheader component="div" disableSticky className="brutos-sidebar-subheader">
                Módulos Brutos
              </ListSubheader>
              {suiteModuleItems.map((item) => (
                (() => {
                  const visual = getSidebarVisual(item);
                  const SidebarIcon = visual.icon;
                  const active = isActiveRoute(item.path);

                  return (
                    <ListItem key={item.path} disablePadding className="brutos-sidebar-list-item">
                      <ListItemButton
                        component={Link}
                        to={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        selected={active}
                        aria-current={active ? 'page' : undefined}
                        onClick={handleSidebarNavigation}
                        className={`brutos-sidebar-link brutos-sidebar-link--${visual.tone}`}
                      >
                        <ListItemIcon className="brutos-sidebar-list-icon">
                          <Box component="span" className="brutos-sidebar-icon-tile">
                            <SidebarIcon fontSize="small" />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          slotProps={{ primary: { className: 'brutos-sidebar-link-label' } }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })()
              ))}
            </>
          ) : null}
          </List>
        </Box>

        {auth.user ? (
          <Box className="brutos-sidebar-account">
            <Stack direction="row" spacing={1.25} alignItems="center" className="brutos-sidebar-account-row">
              <Avatar className="brutos-sidebar-avatar">
                {String(auth.user.login || '?').slice(0, 1).toUpperCase()}
              </Avatar>
              <Box className="brutos-sidebar-account-copy">
                <Typography className="brutos-sidebar-account-login" noWrap>
                  {auth.user.login}
                </Typography>
                <Typography className="brutos-sidebar-account-role" noWrap>
                  {auth.user.role}
                </Typography>
              </Box>
            </Stack>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<LogoutRoundedIcon />}
              onClick={() => auth.logout()}
              className="brutos-sidebar-logout"
            >
              Sair
            </Button>
          </Box>
        ) : null}
      </Drawer>
      <Box component="main" sx={{
        flexGrow: 1,
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
        pt: isStandaloneRoute || hasInlinePageMenu || location.pathname === '/balcao' ? 0 : location.pathname === '/pedidos' ? 0.5 : 3,
        pb: isStandaloneRoute || hasInlinePageMenu || location.pathname === '/pedidos' || location.pathname === '/balcao' ? 0 : 3,
        pr: isStandaloneRoute || hasInlinePageMenu || location.pathname === '/pedidos' || location.pathname === '/balcao' ? 0 : { xs: 1, sm: 2, md: 3 },
        pl: 0,
        transition: 'padding-left 0.3s'
      }}>
        {showMainToolbar ? (
          <Toolbar sx={{ pl: 0 }}>
            <IconButton
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? 'Fechar menu lateral' : 'Abrir menu lateral'}
              sx={{ ml: 0 }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        ) : null}
        <Container maxWidth={false} disableGutters sx={{ px: 0, minWidth: 0, maxWidth: '100%' }}>
          <BillingNoticeBanner enabled={showAppNavigation} />
          <PageMenuContext.Provider value={{
            enabled: hasInlinePageMenu,
            sidebarOpen,
            onToggleSidebar: () => setSidebarOpen((current) => !current),
          }}>
          <Routes>
            <Route path="/mesa" element={<PublicTableMenu />} />
            <Route path="/kds/acesso" element={<KdsAccess />} />
            <Route path="/garcom/acesso" element={<WaiterAccess />} />
            <Route path="/garcom/operacao" element={<WaiterOperation />} />
            <Route path="/login" element={<Login />} />
            <Route path="/recuperar-senha" element={<RecuperarSenha />} />
            <Route path="/redefinir-senha" element={<RedefinirSenha />} />
            <Route
              path="/pedidos"
              element={<ProtectedRoute allow={canAccessBackoffice}><Pedidos sidebarOpen={sidebarOpen} onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} /></ProtectedRoute>}
            />
            <Route
              path="/avaliacoes"
              element={<ProtectedRoute allow={canAccessBackoffice}><Avaliacoes /></ProtectedRoute>}
            />
            <Route
              path="/balcao"
              element={<ProtectedRoute allow={canAccessBackoffice}><Balcao sidebarOpen={sidebarOpen} onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} /></ProtectedRoute>}
            />
            <Route
              path="/salao"
              element={<ProtectedRoute allow={canAccessSalon}><Salao /></ProtectedRoute>}
            />
            <Route
              path="/garcom"
              element={<ProtectedRoute allow={canAccessWaiter}><Garcom /></ProtectedRoute>}
            />
            <Route
              path="/kds"
              element={<ProtectedRoute allow={canAccessBackoffice}><Kds /></ProtectedRoute>}
            />
            <Route
              path="/kds/monitor"
              element={<KdsMonitor />}
            />
            <Route
              path="/cardapio"
              element={<ProtectedRoute allow={canAccessBackoffice}><Cardapio /></ProtectedRoute>}
            />
            <Route
              path="/estoque"
              element={<ProtectedRoute allow={canAccessBackoffice}><Estoque /></ProtectedRoute>}
            />
            <Route
              path="/clientes"
              element={<ProtectedRoute allow={canAccessBackoffice}><Clientes /></ProtectedRoute>}
            />
            <Route
              path="/funcionarios"
              element={<ProtectedRoute allow={canAccessBackoffice}><Funcionarios /></ProtectedRoute>}
            />
            <Route
              path="/financeiro"
              element={<ProtectedRoute allow={canAccessFinance}><Financeiro /></ProtectedRoute>}
            />
            <Route
              path="/configuracoes"
              element={<ProtectedRoute allow={canAccessBackoffice}><Configuracoes /></ProtectedRoute>}
            />
            <Route
              path="/catalog"
              element={<ProtectedRoute allow={canAccessIfoodCatalog}><CatalogHomologacao /></ProtectedRoute>}
            />
            <Route
              path="/suite/entregas"
              element={<ProtectedRoute allow={canAccessBackoffice}><SuiteModuleLauncher moduleId="entregas" /></ProtectedRoute>}
            />
            <Route
              path="/suite/cardapio-digital"
              element={<ProtectedRoute allow={canAccessBackoffice}><SuiteModuleLauncher moduleId="cardapio-digital" /></ProtectedRoute>}
            />
            <Route
              path="/suite/zap"
              element={<ProtectedRoute allow={canAccessBackoffice}><SuiteModuleLauncher moduleId="zap" /></ProtectedRoute>}
            />
            <Route
              path="/trocar-senha"
              element={<ProtectedRoute><TrocarSenha /></ProtectedRoute>}
            />
            <Route path="/" element={<Navigate to={auth.isAuthenticated ? defaultAuthenticatedPath : '/login'} replace />} />
          </Routes>
          </PageMenuContext.Provider>
        </Container>
      </Box>
    </Box>
  );
}

export default App;
